import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { BarChart2, Ruler, Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './ConcreteVolumeCalculatorPage/components/CalculationForm';
import ResultsOverview from './ConcreteVolumeCalculatorPage/components/ResultsOverview';
import StructureVisualization from './ConcreteVolumeCalculatorPage/components/StructureVisualization';
import RelatedMaterialsSidebar from './ConcreteVolumeCalculatorPage/components/RelatedMaterialsSidebar';
import ProjectSummary from './ConcreteVolumeCalculatorPage/components/ProjectSummary';
import CostEstimationSidebar from './ConcreteVolumeCalculatorPage/components/CostEstimationSidebar';
import TipsSidebar from './ConcreteVolumeCalculatorPage/components/TipsSidebar';
import InfoContent from './ConcreteVolumeCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  shapeType: 'rectangle', // rectangle, cylinder, l_shape, arbitrary
  length: '5',  // метры
  width: '5',   // метры
  height: '0.15', // метры
  diameter: '3',  // метры для цилиндра
  thickness: '0.15', // метры для плиты
  radius: '1.5',  // метры для цилиндра
  lShapePrimaryLength: '5', // метры для L-образной формы
  lShapePrimaryWidth: '3', // метры для L-образной формы
  lShapeSecondaryLength: '2', // метры для L-образной формы
  lShapeSecondaryWidth: '2', // метры для L-образной формы
  customSections: [{
    length: '1',
    width: '1',
    height: '0.15',
    quantity: 1
  }],
  concreteGrade: 'M200', // марка бетона
  reinforcementType: 'mesh', // mesh, bars, fiber, none
  reinforcementDensity: 'standard', // light, standard, heavy
  includeWastage: true,
  wastagePercent: '10',
  projectType: 'foundation', // foundation, floor, wall, etc.
  deliveryDistance: '15', // километры
  regionPrice: 'moscow', // москва, спб, и т.д.
  includeDelivery: true,
  includePump: false,
  includeLabor: false,
  laborCost: '1500' // рублей за кубометр
};

// Определение типов конструкций
export const projectTypes = [
  { id: 'foundation', name: 'Фундамент' },
  { id: 'floor', name: 'Плита перекрытия/пол' },
  { id: 'wall', name: 'Стена' },
  { id: 'column', name: 'Колонна' },
  { id: 'stairs', name: 'Лестница' },
  { id: 'path', name: 'Дорожка/отмостка' },
  { id: 'pool', name: 'Бассейн' }
];

// Определение марок бетона
export const concreteGrades = [
  { id: 'M100', name: 'M100 / B7,5', strength: 'Низкая', purpose: 'Подготовительные работы', description: 'Для подбетонки, стяжек, дорожек, неответственных конструкций' },
  { id: 'M150', name: 'M150 / B10', strength: 'Низкая', purpose: 'Подготовительные работы', description: 'Для подготовительных работ, стяжек, неответственных конструкций' },
  { id: 'M200', name: 'M200 / B15', strength: 'Средняя', purpose: 'Фундаменты малоэтажных строений', description: 'Для фундаментов частных домов, заборов, отмосток' },
  { id: 'M250', name: 'M250 / B20', strength: 'Высокая', purpose: 'Капитальное строительство', description: 'Для фундаментов, монолитных стен, перекрытий' },
  { id: 'M300', name: 'M300 / B22,5', strength: 'Высокая', purpose: 'Капитальное строительство', description: 'Для несущих конструкций, фундаментов многоэтажных зданий' },
  { id: 'M350', name: 'M350 / B25', strength: 'Очень высокая', purpose: 'Высотное строительство', description: 'Для высоконагруженных конструкций, мостов, промзданий' },
  { id: 'M400', name: 'M400 / B30', strength: 'Очень высокая', purpose: 'Спец. строительство', description: 'Для особо ответственных конструкций, гидротехнических сооружений' }
];

// Определение типов армирования
export const reinforcementTypes = [
  { id: 'none', name: 'Без армирования' },
  { id: 'mesh', name: 'Сетка', description: 'Стандартная арматурная сетка' },
  { id: 'bars', name: 'Арматурный каркас', description: 'Связанные арматурные стержни' },
  { id: 'fiber', name: 'Фиброволокно', description: 'Полипропиленовое или стальное фиброволокно' }
];

// Определение регионов для цен
export const regions = [
  { id: 'moscow', name: 'Москва', priceModifier: 1.0 },
  { id: 'moscow_region', name: 'Московская область', priceModifier: 0.9 },
  { id: 'spb', name: 'Санкт-Петербург', priceModifier: 0.95 },
  { id: 'spb_region', name: 'Ленинградская область', priceModifier: 0.85 },
  { id: 'central', name: 'Центральный регион', priceModifier: 0.8 },
  { id: 'south', name: 'Южный регион', priceModifier: 0.75 },
  { id: 'north', name: 'Северный регион', priceModifier: 0.85 },
  { id: 'ural', name: 'Урал', priceModifier: 0.8 },
  { id: 'siberia', name: 'Сибирь', priceModifier: 0.85 },
  { id: 'far_east', name: 'Дальний восток', priceModifier: 1.1 }
];

// Базовые цены за куб бетона по маркам (для Москвы)
export const concretePriceBase = {
  'M100': 3800,
  'M150': 4000,
  'M200': 4300,
  'M250': 4600,
  'M300': 4900,
  'M350': 5300,
  'M400': 5800
};

/**
 * ConcreteVolumeCalculatorPage Component - Полный калькулятор объема бетона с расчетом стоимости
 * и рекомендациями по заливке.
 */
const ConcreteVolumeCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('concrete-calculator') || {
    id: 'concrete-calculator',
    name: 'Калькулятор объема бетона',
    metaTitle: 'Калькулятор объема бетона | Расчет количества с учетом марки и стоимости доставки',
    metaDescription: 'Онлайн калькулятор для расчета объема бетона для фундамента, плиты, стен и других конструкций. Учет стоимости бетона разных марок и доставки в вашем регионе России.'
  }, []);

  const pageTitle = tool?.metaTitle || 'Калькулятор объема бетона | Gensio';
  const metaDescription = tool?.metaDescription || 'Онлайн калькулятор для расчета объема бетона для фундамента, плиты, стен и других конструкций. Учет стоимости бетона разных марок и доставки в вашем регионе России.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [errors, setErrors] = useState({});
  const [savedProjects, setSavedProjects] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных проектов из localStorage ---
  useEffect(() => {
    const savedProjectsData = localStorage.getItem('savedConcreteProjects');
    if (savedProjectsData) {
      try {
        setSavedProjects(JSON.parse(savedProjectsData));
      } catch (e) {
        console.error("Error loading saved projects:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'foundation':
          newState.projectType = 'foundation';
          newState.concreteGrade = 'M200';
          newState.reinforcementType = 'mesh';
          break;
        case 'floor':
          newState.projectType = 'floor';
          newState.concreteGrade = 'M250';
          newState.height = '0.1';
          break;
        case 'wall':
          newState.projectType = 'wall';
          newState.concreteGrade = 'M300';
          newState.height = '3';
          newState.width = '0.3';
          break;
        case 'cylinder':
          newState.shapeType = 'cylinder';
          break;
        case 'custom':
          newState.shapeType = 'arbitrary';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение проекта в localStorage
  const saveProject = (name) => {
    if (!results) return;

    const newSavedProject = {
      id: Date.now().toString(),
      name: name || `Расчет бетона для ${
        projectTypes.find(t => t.id === formData.projectType)?.name || 'проекта'
      }`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedProjects = [...savedProjects, newSavedProject];
    setSavedProjects(updatedSavedProjects);
    localStorage.setItem('savedConcreteProjects', JSON.stringify(updatedSavedProjects));

    return newSavedProject;
  };

  // Удаление сохраненного проекта
  const deleteSavedProject = (id) => {
    const updatedSavedProjects = savedProjects.filter(project => project.id !== id);
    setSavedProjects(updatedSavedProjects);
    localStorage.setItem('savedConcreteProjects', JSON.stringify(updatedSavedProjects));
  };

  // Загрузка сохраненного проекта
  const loadSavedProject = (id) => {
    const projectToLoad = savedProjects.find(project => project.id === id);
    if (projectToLoad) {
      setFormData(projectToLoad.formData);
      setResults(projectToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Ruler className="w-8 h-8 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор объема бетона'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Рассчитайте точный объем бетона для вашего строительного проекта. Учтите стоимость материалов, доставки и получите советы по выбору марки бетона для разных типов конструкций.
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              projectTypes={projectTypes}
              concreteGrades={concreteGrades}
              reinforcementTypes={reinforcementTypes}
              regions={regions}
              concretePriceBase={concretePriceBase}
              saveProject={saveProject}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <StructureVisualization 
                  formData={formData} 
                  results={results}
                />
                <ProjectSummary
                  formData={formData}
                  results={results}
                  projectTypes={projectTypes}
                  concreteGrades={concreteGrades}
                  reinforcementTypes={reinforcementTypes}
                />
              </>
            )}

            {/* --- Информационный контент о бетоне --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Оценка стоимости --- */}
            <CostEstimationSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              results={results}
              regions={regions}
              concretePriceBase={concretePriceBase}
            />

            {/* --- Связанные материалы --- */}
            <RelatedMaterialsSidebar 
              formData={formData}
              projectType={formData.projectType}
              concreteGrade={formData.concreteGrade}
              savedProjects={savedProjects}
              deleteSavedProject={deleteSavedProject}
              loadSavedProject={loadSavedProject}
            />

            {/* --- Полезные советы --- */}
            <TipsSidebar
              projectType={formData.projectType}
              concreteGrade={formData.concreteGrade}
            />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Structure Visualization Styles */
        .structure-visualization {
          @apply w-full border rounded-lg bg-white dark:bg-gray-800 overflow-hidden;
        }
      `}</style>
    </div>
  );
};

export default ConcreteVolumeCalculatorPage;